import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Send } from "lucide-react";
import api from "@/lib/api";
import { RepairCard } from "@/components/client/RepairReport";
import { toast } from "sonner";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { DetailHeader } from "@/components/shared/PageChrome";
import { SectorCard } from "@/components/operational/EvidenceCards";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

function Field({ label, value }) {
  return (<div><p className="text-xs font-medium uppercase tracking-wide text-jigsaw-muted">{label}</p><p className="mt-1 text-sm text-jigsaw-navy">{value || "—"}</p></div>);
}

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", job_title: "", is_primary: false });
  const [support, setSupport] = useState(null);
  const [internalNote, setInternalNote] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get(`/directory/clients/${id}`).then((r) => setClient(r.data)).catch(() => setError(true));
  const setRepairStatus = async (noteId, status) => {
    try { await api.post(`/directory/clients/${id}/support/${noteId}/repair-status`, { status }); toast.success(`Repair report ${status.toLowerCase()}`); loadSupport(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update the repair report"); }
  };
  const loadSupport = () => api.get(`/directory/clients/${id}/support`).then((r) => {
    setSupport(r.data.items);
    // Explicit read: this page view IS the staff member opening/reading this client's thread.
    api.post(`/directory/clients/${id}/support/read`).catch(() => {});
  }).catch(() => {});
  useEffect(() => { load(); loadSupport(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    try {
      await api.post(`/directory/clients/${id}/contacts`, form);
      toast.success("Contact added");
      setOpen(false);
      setForm({ full_name: "", email: "", phone: "", job_title: "", is_primary: false });
      load();
    } catch (e) { const d = e?.response?.data?.detail; toast.error(Array.isArray(d) ? d.map((x) => x.msg?.replace(/^Value error, /, "")).join("; ") : d || "Could not add contact"); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Please enter a reply before sending.");
    setSending(true);
    try {
      await api.post(`/directory/clients/${id}/support`, { body: reply, internal: internalNote });
      setReply("");
      toast.success("Reply sent to the client");
      loadSupport();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send reply"); }
    setSending(false);
  };

  if (error) return <ErrorState testId="client-detail-error" />;
  if (!client) return <LoadingState testId="client-detail-loading" />;

  return (
    <div data-testid="client-detail-page" className="space-y-5">
      <DetailHeader
        testId="client-detail-header"
        crumbs={[{ label: "Clients", to: "/control-centre/clients" }, { label: client.name }]}
        chips={client.tier && <span className="rounded-full border border-jigsaw-border px-2.5 py-0.5 text-[12px] text-jigsaw-muted">Tier {client.tier}</span>}
        title={client.name}
        meta={[
          { label: "Tier", value: client.tier },
          { label: "Client since", value: client.client_since },
          { label: "Legacy reference", value: client.legacy_reference, mono: true },
        ]}
        actions={<button onClick={() => navigate("/control-centre/clients")} data-testid="client-back-button" className="sr-only">Back to Clients</button>}
      />
      {!client.legacy_reference && <SectorCard client={client} onSaved={load} />}

      <Card className="border-jigsaw-border">
        <CardHeader><CardTitle className="text-base text-jigsaw-navy">Profile</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-5 md:grid-cols-4">
          <Field label="Tier" value={client.tier} />
          <Field label="Client since" value={client.client_since} />
          <Field label="Legacy reference" value={client.legacy_reference} />
        </CardContent>
      </Card>

      <Card className="border-jigsaw-border">
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-base text-jigsaw-navy">Contacts</CardTitle>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-client-contact-button" size="sm" variant="outline"><Plus className="mr-1 h-3.5 w-3.5" /> Add Contact</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add client contact</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Full name</Label><Input data-testid="client-contact-name-input" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} /></div>
                <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Job title</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
              </div>
              <DialogFooter><Button data-testid="client-contact-save-button" onClick={submit} disabled={!form.full_name}>Save</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent className="space-y-2">
          {client.contacts.length === 0 && <p className="text-sm text-jigsaw-muted">No contacts recorded yet.</p>}
          {client.contacts.map((c) => (
            <div key={c.id} className="flex items-center justify-between rounded-md border border-jigsaw-border px-3 py-2 text-sm">
              <span className="text-jigsaw-navy">{c.full_name} {c.job_title ? `· ${c.job_title}` : ""}</span>
              <span className="text-xs text-jigsaw-muted">{c.email}</span>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card className="border-jigsaw-border">
        <CardHeader><CardTitle className="text-base text-jigsaw-navy">Support messages</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!support && <p className="text-sm text-jigsaw-muted">Loading...</p>}
          {support && support.length === 0 && <p className="text-sm text-jigsaw-muted">No support messages from this client yet.</p>}
          {support && support.map((n) => n.kind === "REPAIR_REPORT" ? (
            <RepairCard key={n.id} note={n}>
              <div className="mt-3 flex flex-wrap gap-2">
                {n.repair?.status === "SUBMITTED" && <Button data-testid={`repair-accept-${n.id}`} size="sm" variant="outline" onClick={() => setRepairStatus(n.id, "ACCEPTED")}>Accept repair</Button>}
                {n.repair?.status !== "RESOLVED" && <Button data-testid={`repair-resolve-${n.id}`} size="sm" onClick={() => setRepairStatus(n.id, "RESOLVED")}>Mark resolved</Button>}
              </div>
            </RepairCard>
          ) : (
            <div key={n.id} data-testid={`staff-support-message-${n.id}`} className={`rounded-lg border p-3 text-sm ${n.is_staff_reply ? "border-jigsaw-orange/30 bg-jigsaw-orange/5" : "border-jigsaw-border bg-white"}`}>
              <p className="flex flex-wrap items-center gap-2 text-xs font-medium text-jigsaw-muted">{n.is_staff_reply ? (n.author_name || "Jigsaw staff") : (n.author_name || "Client")}{n.internal && <span data-testid={`staff-support-internal-${n.id}`} className="rounded-full border border-jigsaw-border bg-jigsaw-bg px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-jigsaw-navy">Internal - not visible to client</span>}</p>
              <p className="mt-1 text-jigsaw-navy whitespace-pre-wrap">{n.body}</p>
              <p className="mt-1 text-xs text-jigsaw-muted">{new Date(n.created_at).toLocaleString("en-GB")}</p>
            </div>
          ))}
          <div className="flex gap-2 pt-1">
            <div className="flex-1 space-y-1.5">
              <Textarea data-testid="staff-support-reply-input" rows={2} placeholder={internalNote ? "Internal note for Jigsaw staff only..." : "Reply to this client..."} value={reply} onChange={(e) => setReply(e.target.value)} />
              <label className="flex items-center gap-2 text-[12px] text-jigsaw-muted"><input type="checkbox" data-testid="staff-support-internal-toggle" className="h-3.5 w-3.5" checked={internalNote} onChange={(e) => setInternalNote(e.target.checked)} /> Internal note - never shown to the client</label>
            </div>
            <Button data-testid="staff-support-reply-button" onClick={sendReply} disabled={sending} className="self-end">
              <Send className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
